import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import TourCard from '../../components/base/TourCard';
import { gunubirlikTours, konaklamaliTours, ulasimliTours, yurtdisiTours } from '../../mocks/tours';

const stats = [
  { value: '50+', label: 'Tur Seçeneği', icon: 'ri-map-2-line' },
  { value: '10K+', label: 'Mutlu Müşteri', icon: 'ri-user-smile-line' },
  { value: '7/24', label: 'Destek Hattı', icon: 'ri-customer-service-2-line' },
  { value: '4.9', label: 'Müşteri Puanı', icon: 'ri-star-fill' },
];

const categories = [
  {
    label: 'Günübirlik Turlar',
    desc: '1 günde eşsiz keşifler',
    icon: 'ri-sun-line',
    path: '/gunubirlik-turlar',
    color: 'from-amber-400 to-orange-500',
    image: 'https://public.readdy.ai/ai/img_res/ce01aabf6daf0ef88e9b68477ab4e6a6.jpg',
  },
  {
    label: 'Konaklamalı Turlar',
    desc: '2-5 gece tarihi güzellikler',
    icon: 'ri-hotel-bed-line',
    path: '/konaklamali-turlar',
    color: 'from-teal-400 to-emerald-500',
    image: 'https://readdy.ai/api/search-image?query=Turkey%20Cappadocia%20overnight%20tour%20hotel%20cave%20boutique%20accommodation%20scenic%20valley%20sunset%20beautiful&width=600&height=400&seq=cat2&orientation=landscape',
  },
  {
    label: 'Ulaşımlı Tatil Paketleri',
    desc: 'Her şey dahil tatil keyfi',
    icon: 'ri-bus-line',
    path: '/ulasimli-tatil',
    color: 'from-rose-400 to-pink-500',
    image: 'https://readdy.ai/api/search-image?query=Turkey%20Mediterranean%20beach%20resort%20Bodrum%20Antalya%20Fethiye%20turquoise%20coast%20travel%20package%20holiday%20summer%20luxury&width=600&height=400&seq=cat3&orientation=landscape',
  },
  {
    label: 'Yurt Dışı Turlar',
    desc: 'Avrupa ve dünya keşifleri',
    icon: 'ri-flight-takeoff-line',
    path: '/yurtdisi-turlar',
    color: 'from-violet-400 to-indigo-500',
    image: 'https://readdy.ai/api/search-image?query=Europe%20travel%20Paris%20Eiffel%20Tower%20international%20tour%20group%20sightseeing%20landmark%20sunset%20cityscape%20travel%20adventure&width=600&height=400&seq=cat4&orientation=landscape',
  },
];

export default function Home() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schemaOrg = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'TravelAgency',
        '@id': `${siteUrl}/#organization`,
        name: 'Ankara Travelers',
        url: siteUrl,
        logo: `${siteUrl}/vite.svg`,
        description: 'Ankara çıkışlı günübirlik, konaklamalı, ulaşımlı tatil paketleri ve yurt dışı turlar. 12 yıllık deneyim, TÜRSAB üyesi güvenilir seyahat acentası.',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Kızılay',
          addressLocality: 'Ankara',
          addressCountry: 'TR',
        },
        telephone: '+905400015806',
        email: 'info@ankaratravelers.com',
        foundingDate: '2013',
        areaServed: { '@type': 'Country', name: 'Turkey' },
        openingHoursSpecification: [
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
            opens: '09:00',
            closes: '18:00',
          },
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: 'Saturday',
            opens: '10:00',
            closes: '16:00',
          },
        ],
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.9',
          reviewCount: '15000',
        },
      },
      {
        '@type': 'WebSite',
        '@id': `${siteUrl}/#website`,
        url: siteUrl,
        name: 'Ankara Travelers',
        inLanguage: 'tr-TR',
        publisher: { '@id': `${siteUrl}/#organization` },
        potentialAction: {
          '@type': 'SearchAction',
          target: `${siteUrl}/ara?q={search_term_string}`,
          'query-input': 'required name=search_term_string',
        },
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-300">
      <title>Ankara Travelers | Ankara Çıkışlı Tur Paketleri ve Seyahat</title>
      <meta name="description" content="Ankara'dan günübirlik turlar, konaklamalı turlar, ulaşımlı tatil paketleri ve yurt dışı tur paketleri. 12 yıllık deneyim, 15.000+ mutlu müşteri, TÜRSAB üyesi Ankara seyahat acentası." />
      <meta name="keywords" content="Ankara tur, Ankara çıkışlı turlar, günübirlik tur, yurt dışı tur, tatil paketi" />
      <link rel="canonical" href={`${siteUrl}/`} />
      <meta property="og:title" content="Ankara Travelers | Ankara Çıkışlı Tur Paketleri" />
      <meta property="og:description" content="Ankara'dan günübirlik, konaklamalı ve yurt dışı tur paketleri. TÜRSAB üyesi, 12 yıllık deneyim." />
      <meta property="og:url" content={`${siteUrl}/`} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaOrg) }}
      />
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/08ee16311ccf6fed6ba22c2912a14162/d99713382529cf789359315fdaac1b17.jpeg"
            alt="Ankara Travelers Hero"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/20" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full pt-20">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-orange-500/20 backdrop-blur-sm border border-orange-400/30 text-orange-300 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-map-pin-line text-sm" />
              </div>
              Ankara Çıkışlı Tur Paketleri
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white leading-tight mb-6">
              <span className="text-orange-400">Ankara&apos;dan</span><br />
              Dünyaya Açılan<br />
              Kapınız
            </h1>
            <p className="text-lg text-gray-300 mb-10 leading-relaxed max-w-lg">
              Günübirlik gezilerden yurt dışı turlarına kadar hayalinizdeki tatili en uygun fiyatlarla keşfedin.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/gunubirlik-turlar"
                className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-full font-semibold text-base transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                Turları Keşfet
              </Link>
              <a
                href="#kategoriler"
                className="bg-white/10 backdrop-blur-sm hover:bg-white/20 border border-white/30 text-white px-8 py-4 rounded-full font-semibold text-base transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Kategoriler
              </a>
            </div>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="absolute bottom-0 left-0 right-0 bg-white/10 backdrop-blur-md border-t border-white/20">
          <div className="max-w-7xl mx-auto px-6 py-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {stats.map(stat => (
                <div key={stat.label} className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-orange-500/20 rounded-xl">
                    <i className={`${stat.icon} text-orange-400 text-lg`} />
                  </div>
                  <div>
                    <p className="text-white font-bold text-xl leading-none">{stat.value}</p>
                    <p className="text-gray-300 text-xs mt-0.5">{stat.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section id="kategoriler" className="py-24 bg-gray-50 dark:bg-gray-800 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Tur <span className="text-orange-500">Kategorilerimiz</span>
            </h2>
            <p className="text-gray-500 dark:text-gray-400 text-lg max-w-xl mx-auto">
              Her bütçeye ve zevke uygun tur seçenekleri sizi bekliyor
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.map(cat => (
              <Link
                key={cat.path}
                to={cat.path}
                className="group relative rounded-2xl overflow-hidden cursor-pointer h-72 block"
              >
                <img
                  src={cat.image}
                  alt={cat.label}
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <div className="w-10 h-10 flex items-center justify-center bg-orange-500 rounded-xl mb-3">
                    <i className={`${cat.icon} text-white text-lg`} />
                  </div>
                  <h3 className="text-white font-bold text-base mb-1">{cat.label}</h3>
                  <p className="text-gray-300 text-xs">{cat.desc}</p>
                </div>
                <div className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <i className="ri-arrow-right-line text-white text-sm" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Günübirlik Turlar Preview */}
      <section className="py-24 dark:bg-gray-900 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between mb-10">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
                Günübirlik <span className="text-orange-500">Turlar</span>
              </h2>
              <p className="text-gray-500 dark:text-gray-400">Ankara çıkışlı en popüler günübirlik turlar</p>
            </div>
            <Link
              to="/gunubirlik-turlar"
              className="hidden md:flex items-center gap-2 text-orange-500 hover:text-orange-600 font-semibold text-sm cursor-pointer whitespace-nowrap"
            >
              Tümünü Gör
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line" />
              </div>
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {gunubirlikTours.slice(0, 4).map(tour => (
              <TourCard key={tour.id} tour={tour} />
            ))}
          </div>
          <div className="text-center mt-8 md:hidden">
            <Link
              to="/gunubirlik-turlar"
              className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-full font-semibold text-sm cursor-pointer"
            >
              Tüm Günübirlik Turlar
            </Link>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-orange-500">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            {[
              { icon: 'ri-shield-check-line', title: 'Güvenli Rezervasyon', desc: 'Tüm ödemeleriniz SSL ile şifrelenmiş güvenli altyapı üzerinde işlenir.' },
              { icon: 'ri-customer-service-2-line', title: '7/24 Destek', desc: 'Her an ulaşabileceğiniz deneyimli tur danışmanlarımız hizmetinizde.' },
              { icon: 'ri-award-line', title: 'Kalite Garantisi', desc: '10.000+ memnun müşteriyle Türkiye\'nin güvenilir tur operatörü.' },
            ].map(item => (
              <div key={item.title} className="text-white">
                <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-2xl mx-auto mb-4">
                  <i className={`${item.icon} text-3xl`} />
                </div>
                <h3 className="font-bold text-xl mb-2">{item.title}</h3>
                <p className="text-orange-100 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Konaklamalı Preview */}
      <section className="py-24 bg-gray-50 dark:bg-gray-800 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between mb-10">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
                Konaklamalı <span className="text-orange-500">Turlar</span>
              </h2>
              <p className="text-gray-500 dark:text-gray-400">2-5 gece konaklamalı özel tur paketleri</p>
            </div>
            <Link
              to="/konaklamali-turlar"
              className="hidden md:flex items-center gap-2 text-orange-500 hover:text-orange-600 font-semibold text-sm cursor-pointer whitespace-nowrap"
            >
              Tümünü Gör
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line" />
              </div>
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {konaklamaliTours.slice(0, 3).map(tour => (
              <TourCard key={tour.id} tour={tour} />
            ))}
          </div>
        </div>
      </section>

      {/* Ulaşımlı Preview */}
      <section className="py-24 dark:bg-gray-900 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between mb-10">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
                Ulaşımlı <span className="text-orange-500">Tatil Paketleri</span>
              </h2>
              <p className="text-gray-500 dark:text-gray-400">Ulaşım dahil tatil paketleri ile zahmetsiz tatil</p>
            </div>
            <Link
              to="/ulasimli-tatil"
              className="hidden md:flex items-center gap-2 text-orange-500 hover:text-orange-600 font-semibold text-sm cursor-pointer whitespace-nowrap"
            >
              Tümünü Gör
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line" />
              </div>
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ulasimliTours.slice(0, 3).map(tour => (
              <TourCard key={tour.id} tour={tour} />
            ))}
          </div>
        </div>
      </section>

      {/* Yurt Dışı Preview */}
      <section className="py-24 bg-gray-50 dark:bg-gray-800 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between mb-10">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
                Yurt Dışı <span className="text-orange-500">Turlar</span>
              </h2>
              <p className="text-gray-500 dark:text-gray-400">Avrupa ve dünya destinasyonlarına özel turlar</p>
            </div>
            <Link
              to="/yurtdisi-turlar"
              className="hidden md:flex items-center gap-2 text-orange-500 hover:text-orange-600 font-semibold text-sm cursor-pointer whitespace-nowrap"
            >
              Tümünü Gör
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line" />
              </div>
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {yurtdisiTours.slice(0, 4).map(tour => (
              <TourCard key={tour.id} tour={tour} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-stone-900 to-stone-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://readdy.ai/api/search-image?query=world%20map%20travel%20destinations%20globe%20abstract%20pattern%20dark%20background%20wanderlust%20adventure&width=1400&height=500&seq=cta1&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">
            Hayalinizdeki Tatil<br />
            <span className="text-orange-400">Bir Tık Uzağınızda</span>
          </h2>
          <p className="text-gray-400 text-lg mb-8">
            Hemen rezervasyon yapın, erken rezervasyon fırsatlarından yararlanın.
          </p>
          <Link
            to="/gunubirlik-turlar"
            className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-10 py-4 rounded-full font-bold text-base transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-map-2-line" />
            </div>
            Tüm Turları Keşfet
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
